import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiPlus,
  FiSearch,
  FiFileText,
  FiClock,
  FiDollarSign,
  FiAlertCircle,
  FiChevronLeft,
  FiTrash2,
  FiSave,
  FiEdit2,
  FiRefreshCw,
  FiArrowUpRight,
  FiCheckCircle,
  FiX,
  FiLoader,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { billService } from "../services/billService";
import { receiptService } from "../services/receiptService";
import { useClient } from "../context/ClientContext";

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const newItem = () => ({
  key: crypto.randomUUID(),
  description: "",
  quantity: "1",
  unit_price: "0",
  tax_rate: "0",
});
const newForm = () => ({
  vendor_id: "",
  bill_number: "",
  bill_date: today(),
  due_date: today(),
  currency: "USD",
  tax_rounding_method: "per_line",
  notes: "",
});
const money = (value, currency = "USD") => {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);
  } catch {
    return `${currency} ${(Number(value) || 0).toFixed(2)}`;
  }
};
const dateLabel = (value) => {
  if (!value) return "—";
  const d = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};
const displayStatus = (bill) =>
  ["unpaid", "partially_paid"].includes(bill.status) &&
  Number(bill.balance_due) > 0 &&
  bill.due_date < today()
    ? "overdue"
    : bill.status;
const statusText = {
  draft: "Draft",
  unpaid: "Unpaid",
  partially_paid: "Partially Paid",
  paid: "Paid",
  void: "Void",
  overdue: "Overdue",
};
const statusStyles = {
  draft: "bg-slate-100 text-slate-600",
  unpaid: "bg-amber-50 text-amber-700",
  partially_paid: "bg-sky-50 text-sky-700",
  paid: "bg-emerald-50 text-emerald-700",
  void: "bg-slate-100 text-slate-500",
  overdue: "bg-rose-50 text-rose-700",
};
const fieldClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50";
const Label = ({ children }) => (
  <label className="mb-1.5 block text-xs font-semibold text-slate-600">
    {children}
  </label>
);
const StatusBadge = ({ bill }) => {
  const status = displayStatus(bill);
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[status] || statusStyles.draft}`}
    >
      {statusText[status] || status}
    </span>
  );
};
const SummaryCard = ({ title, value, icon: Icon, detail }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5">
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-500">{title}</span>
      <div className="rounded-xl bg-slate-50 p-2.5 text-slate-500">
        <Icon size={18} />
      </div>
    </div>
    <div className="mt-3 text-2xl font-bold tracking-tight text-slate-900">
      {value}
    </div>
    <p className="mt-1 text-xs text-slate-400">{detail}</p>
  </div>
);
const normalizeVendorName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^\p{L}\p{N}]/gu, "")
    .replace(
      /(incorporated|corporation|company|limited|inc|corp|llc|ltd)$/i,
      "",
    );
const findAIVendor = (draft, vendors) => {
  const active = vendors.filter((v) => v.is_active);
  const byId = active.find((v) => v.id === draft.vendor_id);
  if (byId) return byId;
  const name = normalizeVendorName(draft.detected_vendor || draft.vendor_name);
  if (!name) return null;
  const matches = active.filter((v) =>
    [v.vendor_name, v.company_name].some(
      (candidate) => normalizeVendorName(candidate) === name,
    ),
  );
  return matches.length === 1 ? matches[0] : null;
};
// Decimal-safe money calculations. Inputs use up to four decimal places,
// matching accounting.bill_items NUMERIC columns in Supabase.
const decimalScaled = (value, places = 4) => {
  const text = String(value ?? "0").trim();
  const match = text.match(/^(\d+)(?:\.(\d*))?$/);
  if (!match) return 0n;
  const fraction = (match[2] || "").padEnd(places, "0").slice(0, places);
  return BigInt(match[1]) * 10n ** BigInt(places) + BigInt(fraction || "0");
};
const roundDivide = (numerator, denominator) =>
  (numerator + denominator / 2n) / denominator;
const toCents = (value) => Number(roundDivide(decimalScaled(value, 4), 100n));
const makeAIItems = (draft) => {
  const sourceItems = Array.isArray(draft.line_items) ? draft.line_items : [];
  const numberOrNull = (value) =>
    value === null ||
    value === undefined ||
    value === "" ||
    !Number.isFinite(Number(value))
      ? null
      : Number(value);
  const items = sourceItems.map((source) => {
    const item = newItem();
    const quantity = numberOrNull(source.quantity);
    const price = numberOrNull(source.unit_price);
    const explicitRate = numberOrNull(source.tax_rate);
    const taxAmount = numberOrNull(source.tax_amount);
    let rate = explicitRate;
    if (rate === null && taxAmount !== null && quantity > 0 && price > 0)
      rate = (taxAmount / (quantity * price)) * 100;
    return {
      ...item,
      description: String(source.description || "").trim(),
      quantity: quantity === null ? "" : String(quantity),
      unit_price: price === null ? "" : String(price),
      tax_rate: rate === null ? "" : String(Math.round(rate * 10000) / 10000),
    };
  });
  return {
    items: items.length ? items : [newItem()],
    missingItems: items.length === 0,
  };
};

// Match PostgreSQL NUMERIC rounding exactly for nonnegative amounts.
// Invoice-level tax is rounded cumulatively, once across the full invoice.
const calculateLines = (items, method) => {
  let cumulativeTaxNumerator = 0n;
  let previousRoundedTaxCents = 0n;
  return items.map((item) => {
    const quantity = decimalScaled(item.quantity);
    const price = decimalScaled(item.unit_price);
    const rate = decimalScaled(item.tax_rate);
    const subtotalCents = roundDivide(quantity * price, 1000000n);
    // Tax in cents: subtotal cents * (rate / 100), rate scaled to 4 dp.
    const taxNumerator = subtotalCents * rate;
    let taxCents;
    if (method === "invoice") {
      cumulativeTaxNumerator += taxNumerator;
      const roundedCumulative = roundDivide(cumulativeTaxNumerator, 1000000n);
      taxCents = roundedCumulative - previousRoundedTaxCents;
      previousRoundedTaxCents = roundedCumulative;
    } else {
      taxCents = roundDivide(taxNumerator, 1000000n);
    }
    return {
      subtotal: Number(subtotalCents) / 100,
      tax: Number(taxCents) / 100,
      total: Number(subtotalCents + taxCents) / 100,
    };
  });
};

export default function Bills() {
  const { activeClient } = useClient();
  const organizationId = activeClient?.id;
  const location = useLocation();
  const navigate = useNavigate();
  const organizationRef = useRef(organizationId);
  organizationRef.current = organizationId;
  const [sourceReceiptId, setSourceReceiptId] = useState(null);
  const [sourceOrganizationId, setSourceOrganizationId] = useState(null);
  const [aiReviewRequired, setAiReviewRequired] = useState(false);
  const [aiReviewed, setAiReviewed] = useState(false);
  const [pendingLinkBillId, setPendingLinkBillId] = useState(null);
  const [linkingReceipt, setLinkingReceipt] = useState(false);
  const [savedTotalMismatch, setSavedTotalMismatch] = useState(false);
  const [aiDraftWarning, setAiDraftWarning] = useState("");
  const [aiSourceTotals, setAiSourceTotals] = useState(null);
  const [aiVendorName, setAiVendorName] = useState("");
  const [view, setView] = useState("list");
  const [bills, setBills] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(newForm);
  const [items, setItems] = useState([newItem()]);
  const [currentBill, setCurrentBill] = useState(null);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_date: today(),
    payment_method: "bank_transfer",
    reference_number: "",
    notes: "",
  });

  const loadData = async () => {
    if (!organizationId) return;
    const org = organizationId;
    setLoading(true);
    setError("");
    try {
      const [billRows, vendorRows] = await Promise.all([
        billService.getBills(org),
        billService.getVendors(org),
      ]);
      if (organizationRef.current !== org) return;
      setBills(billRows);
      setVendors(vendorRows);
      setDataReady(true);
    } catch (err) {
      if (organizationRef.current === org)
        setError(err.message || "Unable to load bills.");
    } finally {
      if (organizationRef.current === org) setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setView("list");
    setBills([]);
    setVendors([]);
    setDataReady(false);
    setEditingId(null);
    setCurrentBill(null);
    setSourceReceiptId(null);
    setSourceOrganizationId(null);
    setPendingLinkBillId(null);
    setSavedTotalMismatch(false);
    setAiReviewRequired(false);
    setAiReviewed(false);
    setAiSourceTotals(null);
    setAiVendorName("");
    setError("");
    setSuccess("");
    if (!organizationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      billService.getBills(organizationId),
      billService.getVendors(organizationId),
    ])
      .then(([billRows, vendorRows]) => {
        if (!cancelled) {
          setBills(billRows);
          setVendors(vendorRows);
          setDataReady(true);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Unable to load bills.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    const draft = location.state?.aiBillDraft;
    if (!draft || !organizationId || !dataReady) return;
    if (draft.organizationId !== organizationId) {
      setError(
        "The AI bill draft belongs to another business. Switch to the correct business before continuing.",
      );
      return;
    }
    const { items: extractedItems, missingItems } = makeAIItems(draft);
    const vendor = findAIVendor(draft, vendors);
    setAiVendorName(draft.detected_vendor || draft.vendor_name || "");
    setAiSourceTotals({
      subtotal: draft.subtotal ?? null,
      tax: draft.tax_amount ?? null,
      total: draft.total_amount ?? null,
      currency: draft.currency || "USD",
    });
    setEditingId(null);
    setCurrentBill(null);
    setForm({
      vendor_id: vendor?.id || "",
      bill_number: draft.bill_number || "",
      bill_date: draft.bill_date || today(),
      due_date: draft.due_date || draft.bill_date || today(),
      currency: draft.currency || "USD",
      tax_rounding_method: "invoice",
      notes: "Created from an AI-extracted purchase document.",
    });
    setItems(extractedItems);
    setSourceReceiptId(draft.receiptId);
    setSourceOrganizationId(draft.organizationId);
    setAiReviewRequired(true);
    setAiReviewed(false);
    setPendingLinkBillId(null);
    setSavedTotalMismatch(false);
    setAiDraftWarning(
      missingItems
        ? "The AI could not identify individual line items. Enter the actual items from the document before saving."
        : `${extractedItems.length} individual line item${extractedItems.length === 1 ? "" : "s"} imported. Review quantities, prices, tax rates, and the calculated total before saving.`,
    );
    setError("");
    setSuccess("");
    setView("form");
    navigate(location.pathname, { replace: true, state: null });
  }, [
    location.state,
    location.pathname,
    organizationId,
    dataReady,
    vendors,
    navigate,
  ]);

  const vendorMap = useMemo(
    () => Object.fromEntries(vendors.map((v) => [v.id, v])),
    [vendors],
  );
  const visibleBills = useMemo(() => {
    const query = search.trim().toLowerCase();
    return bills.filter((bill) => {
      const vendor = vendorMap[bill.vendor_id]?.vendor_name || "";
      return (
        (filter === "all" || displayStatus(bill) === filter) &&
        (!query ||
          String(bill.bill_number || "")
            .toLowerCase()
            .includes(query) ||
          vendor.toLowerCase().includes(query))
      );
    });
  }, [bills, vendorMap, search, filter]);
  const summary = useMemo(() => {
    const active = bills.filter((b) => b.status !== "void");
    return {
      count: active.length,
      outstanding: active
        .filter((b) => b.status !== "draft")
        .reduce((sum, b) => sum + Number(b.balance_due || 0), 0),
      overdue: active
        .filter((b) => displayStatus(b) === "overdue")
        .reduce((sum, b) => sum + Number(b.balance_due || 0), 0),
      drafts: active.filter((b) => b.status === "draft").length,
    };
  }, [bills]);
  const currencies = [
    ...new Set(bills.filter((b) => b.status !== "void").map((b) => b.currency)),
  ];
  const summaryCurrency = currencies.length === 1 ? currencies[0] : null;
  const summaryMoney = (value) =>
    summaryCurrency
      ? money(value, summaryCurrency)
      : currencies.length === 0
        ? money(value)
        : "Multiple currencies";

  const calculatedLineItems = useMemo(
    () => calculateLines(items, form.tax_rounding_method),
    [items, form.tax_rounding_method],
  );
  const totals = useMemo(() => {
    const subtotalCents = calculatedLineItems.reduce(
      (sum, line) => sum + toCents(line.subtotal),
      0,
    );
    const taxCents = calculatedLineItems.reduce(
      (sum, line) => sum + toCents(line.tax),
      0,
    );
    return {
      subtotal: subtotalCents / 100,
      tax: taxCents / 100,
      total: (subtotalCents + taxCents) / 100,
    };
  }, [calculatedLineItems]);
  const aiReconciliation = useMemo(() => {
    if (!sourceReceiptId || !aiSourceTotals) return null;
    if (
      aiSourceTotals.total === null ||
      aiSourceTotals.total === "" ||
      !Number.isFinite(Number(aiSourceTotals.total))
    ) {
      return {
        available: false,
        matches: false,
        message:
          "The source invoice total is unavailable. Review the original document before creating the bill.",
      };
    }
    const sourceTotal = Number(aiSourceTotals.total);
    const differenceCents = toCents(totals.total) - toCents(sourceTotal);
    return {
      available: true,
      matches:
        differenceCents === 0 && form.currency === aiSourceTotals.currency,
      differenceCents,
      sourceTotal,
      sourceCurrency: aiSourceTotals.currency,
    };
  }, [sourceReceiptId, aiSourceTotals, totals.total, form.currency]);

  const updateForm = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const updateItem = (key, field, value) =>
    setItems((prev) =>
      prev.map((item) =>
        item.key === key ? { ...item, [field]: value } : item,
      ),
    );
  const resetAI = () => {
    setSourceReceiptId(null);
    setSourceOrganizationId(null);
    setAiReviewRequired(false);
    setAiReviewed(false);
    setPendingLinkBillId(null);
    setSavedTotalMismatch(false);
    setAiDraftWarning("");
    setAiSourceTotals(null);
    setAiVendorName("");
  };
  const openCreate = () => {
    resetAI();
    setEditingId(null);
    setCurrentBill(null);
    setForm(newForm());
    setItems([newItem()]);
    setPayments([]);
    setError("");
    setSuccess("");
    setView("form");
  };
  const openBill = async (bill) => {
    if (!organizationId || opening) return;
    const org = organizationId;
    setOpening(true);
    setError("");
    resetAI();
    try {
      const [full, paymentRows] = await Promise.all([
        billService.getBillById(org, bill.id),
        billService.getBillPayments(org, bill.id),
      ]);
      if (organizationRef.current !== org) return;
      setPayments(paymentRows);
      setCurrentBill(full);
      setEditingId(full.id);
      setForm({
        vendor_id: full.vendor_id,
        bill_number: full.bill_number,
        bill_date: full.bill_date,
        due_date: full.due_date,
        currency: full.currency,
        tax_rounding_method: full.tax_rounding_method || "per_line",
        notes: full.notes || "",
      });
      setItems(
        full.items.length
          ? full.items.map((item) => ({
              key: item.id,
              description: item.description,
              quantity: String(item.quantity),
              unit_price: String(item.unit_price),
              tax_rate: String(item.tax_rate),
            }))
          : [newItem()],
      );
      setView("form");
    } catch (err) {
      if (organizationRef.current === org)
        setError(err.message || "Unable to open bill.");
    } finally {
      if (organizationRef.current === org) setOpening(false);
    }
  };
  const editable =
    !currentBill ||
    (["draft", "unpaid"].includes(currentBill.status) &&
      Number(currentBill.amount_paid) === 0);

  const linkCreatedBillToReceipt = async (billId, orgId, receiptId) => {
    if (!billId || !orgId || !receiptId)
      throw new Error("Missing bill or receipt information.");
    const [savedBill, receipt] = await Promise.all([
      billService.getBillById(orgId, billId),
      receiptService.getReceipt(orgId, receiptId),
    ]);
    if (
      !savedBill ||
      !receipt ||
      savedBill.organization_id !== orgId ||
      receipt.organization_id !== orgId
    )
      throw new Error("Business verification failed.");
    if (receipt.bill_id === billId) return;
    if (receipt.bill_id || receipt.bill_payment_id)
      throw new Error(
        "This receipt is already linked to another accounting record.",
      );
    if (receipt.vendor_id && receipt.vendor_id !== savedBill.vendor_id)
      throw new Error(
        "Receipt vendor differs from the saved bill vendor. Review the receipt before linking.",
      );
    if (receipt.currency && receipt.currency !== savedBill.currency)
      throw new Error("Receipt currency differs from the saved bill currency.");
    const sourceTotal = aiSourceTotals?.total;
    if (
      sourceTotal !== null &&
      sourceTotal !== undefined &&
      sourceTotal !== "" &&
      Number.isFinite(Number(sourceTotal)) &&
      toCents(savedBill.total) !== toCents(sourceTotal)
    ) {
      throw new Error(
        `Saved bill total ${money(savedBill.total, savedBill.currency)} does not match the source invoice ${money(sourceTotal, savedBill.currency)}. Review the bill before linking.`,
      );
    }
    if (
      receipt.amount !== null &&
      receipt.amount !== undefined &&
      toCents(receipt.amount) !== toCents(savedBill.total)
    ) {
      throw new Error(
        "The receipt amount differs from the saved bill total. Review the receipt before linking.",
      );
    }
    await receiptService.updateReceipt(orgId, receiptId, {
      vendor_id: savedBill.vendor_id,
      bill_id: billId,
      bill_payment_id: null,
      receipt_date: receipt.receipt_date || savedBill.bill_date,
      amount:
        receipt.amount == null
          ? Number(savedBill.total)
          : Number(receipt.amount),
      currency: receipt.currency || savedBill.currency,
      notes: receipt.notes || "",
    });
  };
  const retryReceiptLink = async () => {
    if (
      !pendingLinkBillId ||
      savedTotalMismatch ||
      !sourceReceiptId ||
      !organizationId ||
      sourceOrganizationId !== organizationId ||
      linkingReceipt
    )
      return;
    const org = organizationId;
    setLinkingReceipt(true);
    setError("");
    try {
      await linkCreatedBillToReceipt(pendingLinkBillId, org, sourceReceiptId);
      const updatedBills = await billService.getBills(org);
      if (organizationRef.current !== org) return;
      setBills(updatedBills);
      resetAI();
      setEditingId(null);
      setCurrentBill(null);
      setView("list");
      setSuccess("Draft bill created and receipt linked successfully.");
    } catch (err) {
      if (organizationRef.current === org)
        setError(
          `The draft bill already exists, but the receipt is not linked: ${err.message}`,
        );
    } finally {
      if (organizationRef.current === org) setLinkingReceipt(false);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (saving || !editable || pendingLinkBillId) return;
    setError("");
    if (!organizationId) return setError("Select a business before saving.");
    if (sourceReceiptId && sourceOrganizationId !== organizationId)
      return setError(
        "The source receipt belongs to another business. Return to Receipts and start again.",
      );
    if (aiReviewRequired && !aiReviewed)
      return setError(
        "Review and confirm the AI-suggested bill information before saving.",
      );
    if (!form.vendor_id) return setError("Please select a vendor.");
    if (!form.bill_number.trim()) return setError("Bill number is required.");
    if (!form.bill_date || !form.due_date || form.due_date < form.bill_date)
      return setError("Enter valid bill and due dates.");
    if (!["per_line", "invoice"].includes(form.tax_rounding_method))
      return setError("Select a valid tax rounding method.");
    if (
      items.some(
        (item) =>
          !item.description.trim() ||
          item.quantity === "" ||
          item.unit_price === "" ||
          item.tax_rate === "" ||
          ![item.quantity, item.unit_price, item.tax_rate].every((v) =>
            Number.isFinite(Number(v)),
          ) ||
          Number(item.quantity) <= 0 ||
          Number(item.unit_price) < 0 ||
          Number(item.tax_rate) < 0 ||
          Number(item.tax_rate) > 100,
      )
    ) {
      return setError(
        "Complete every line with valid quantities, prices and tax rates.",
      );
    }
    if (sourceReceiptId && !aiReconciliation?.matches)
      return setError(
        "The calculated bill total does not match the original invoice. Review the line items and tax calculation before saving.",
      );
    const org = organizationId;
    setSaving(true);
    try {
      const latestBills = await billService.getBills(org);
      if (organizationRef.current !== org) return;
      if (!editingId) {
        const duplicate = latestBills.find(
          (bill) =>
            String(bill.bill_number || "")
              .trim()
              .toLowerCase() === form.bill_number.trim().toLowerCase(),
        );
        if (duplicate)
          throw new Error(
            `Bill ${duplicate.bill_number} already exists in this business. Review the existing bill instead of creating a duplicate.`,
          );
      }
      const savedBillId = await billService.saveBill(
        org,
        form,
        items,
        editingId,
      );
      if (organizationRef.current !== org) return;
      if (
        typeof savedBillId !== "string" ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          savedBillId,
        )
      )
        throw new Error(
          "The bill save returned an unexpected identifier. Check the Bills list before trying again.",
        );
      if (!sourceReceiptId) {
        const [billRows, vendorRows] = await Promise.all([
          billService.getBills(org),
          billService.getVendors(org),
        ]);
        if (organizationRef.current !== org) return;
        setBills(billRows);
        setVendors(vendorRows);
        setView("list");
        setSuccess(
          editingId
            ? "Bill updated successfully."
            : "Draft bill created successfully.",
        );
        setEditingId(null);
        setCurrentBill(null);
        return;
      }
      // Persist the ID before any verification or receipt linking,
      // preventing accidental duplicate bills on retry.
      setPendingLinkBillId(savedBillId);
      const verifiedBill = await billService.getBillById(org, savedBillId);
      if (organizationRef.current !== org) return;
      if (
        verifiedBill.organization_id !== org ||
        verifiedBill.currency !== form.currency ||
        toCents(verifiedBill.total) !== toCents(totals.total) ||
        toCents(verifiedBill.total) !== toCents(aiSourceTotals?.total)
      ) {
        setSavedTotalMismatch(true);
        throw new Error(
          "The bill was saved, but its database total differs from the original invoice or preview. Do not create another bill or link the receipt until the saved bill is reviewed.",
        );
      }
      try {
        await linkCreatedBillToReceipt(savedBillId, org, sourceReceiptId);
        const updatedBills = await billService.getBills(org);
        if (organizationRef.current !== org) return;
        setBills(updatedBills);
        resetAI();
        setView("list");
        setEditingId(null);
        setCurrentBill(null);
        setSuccess("Draft bill created and receipt linked successfully.");
      } catch (linkError) {
        if (organizationRef.current === org)
          setError(
            `Draft bill ${form.bill_number} was created, but the receipt could not be linked: ${linkError.message}. Use Retry Receipt Link. Do not create another bill.`,
          );
      }
    } catch (err) {
      if (organizationRef.current === org)
        setError(err.message || "Unable to save bill.");
    } finally {
      if (organizationRef.current === org) setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!organizationId || !currentBill?.id || finalizing) return;
    const org = organizationId;
    setFinalizing(true);
    setError("");
    try {
      await billService.finalizeBill(org, currentBill.id);
      const [updatedBill, updatedBills] = await Promise.all([
        billService.getBillById(org, currentBill.id),
        billService.getBills(org),
      ]);
      if (organizationRef.current !== org) return;
      setCurrentBill(updatedBill);
      setBills(updatedBills);
      setShowFinalizeModal(false);
      setSuccess("Bill finalized successfully.");
      setView("list");
    } catch (err) {
      if (organizationRef.current === org) {
        setError(err.message || "Unable to finalize bill.");
        setShowFinalizeModal(false);
      }
    } finally {
      if (organizationRef.current === org) setFinalizing(false);
    }
  };
  const handleRecordPayment = async (event) => {
    event.preventDefault();
    if (!organizationId || !currentBill?.id || recordingPayment) return;
    setError("");
    const amount = Number(paymentForm.amount);
    const balance = Number(currentBill.balance_due);
    if (
      !Number.isFinite(amount) ||
      amount <= 0 ||
      Math.abs(amount * 100 - Math.round(amount * 100)) > 1e-7 ||
      amount > balance
    )
      return setError(
        "Enter a valid payment amount that does not exceed the outstanding balance.",
      );
    if (!paymentForm.payment_date) return setError("Payment date is required.");
    const org = organizationId;
    setRecordingPayment(true);
    try {
      await billService.recordBillPayment(org, currentBill.id, paymentForm);
      const [updatedBill, updatedBills, paymentRows] = await Promise.all([
        billService.getBillById(org, currentBill.id),
        billService.getBills(org),
        billService.getBillPayments(org, currentBill.id),
      ]);
      if (organizationRef.current !== org) return;
      setCurrentBill(updatedBill);
      setBills(updatedBills);
      setPayments(paymentRows);
      setShowPaymentModal(false);
      setPaymentForm({
        amount: "",
        payment_date: today(),
        payment_method: "bank_transfer",
        reference_number: "",
        notes: "",
      });
      setSuccess("Vendor payment recorded successfully.");
      setView("list");
    } catch (err) {
      if (organizationRef.current === org) {
        setError(err.message || "Unable to record payment.");
        setShowPaymentModal(false);
      }
    } finally {
      if (organizationRef.current === org) setRecordingPayment(false);
    }
  };

  if (!organizationId)
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <FiFileText className="mb-4 text-4xl text-slate-300" />
        <h2 className="text-lg font-semibold text-slate-800">
          Select a business
        </h2>
        <p className="mt-2 text-sm text-slate-500">
          Choose a business to manage its bills.
        </p>
      </div>
    );

  if (view === "form")
    return (
      <div className="mx-auto max-w-6xl pb-24">
        <button
          type="button"
          onClick={() => {
            setView("list");
            setError("");
          }}
          className="mb-6 flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900"
        >
          <FiChevronLeft /> Back to bills
        </button>
        <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              {editingId ? "Bill details" : "Create bill"}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {editingId
                ? "Review and manage this vendor bill."
                : "Record a bill received from a vendor."}
            </p>
          </div>
          {currentBill ? (
            <StatusBadge bill={currentBill} />
          ) : (
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              New draft
            </span>
          )}
        </div>
        {error && (
          <div className="mb-5 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            <FiAlertCircle className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {sourceReceiptId && (
          <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-5">
            <div className="flex items-start gap-3">
              <FiFileText className="mt-0.5 shrink-0 text-blue-600" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-blue-900">
                  AI-Assisted Draft Bill
                </h3>
                <p className="mt-2 text-sm leading-6 text-blue-800">
                  {aiDraftWarning}
                </p>
                {pendingLinkBillId ? (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-semibold text-amber-800">
                      The draft bill has already been created. Do not save
                      another copy.
                    </p>
                    {!savedTotalMismatch && (
                      <button
                        type="button"
                        disabled={linkingReceipt}
                        onClick={retryReceiptLink}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        {linkingReceipt ? (
                          <FiLoader className="animate-spin" />
                        ) : (
                          <FiRefreshCw />
                        )}
                        {linkingReceipt
                          ? "Linking receipt..."
                          : "Retry Receipt Link"}
                      </button>
                    )}
                  </div>
                ) : (
                  <label className="mt-4 flex items-start gap-3 text-sm font-medium text-blue-900">
                    <input
                      type="checkbox"
                      checked={aiReviewed}
                      onChange={(e) => setAiReviewed(e.target.checked)}
                      className="mt-1"
                    />
                    I have reviewed the vendor, bill number, dates, currency,
                    line items, tax, and total.
                  </label>
                )}
              </div>
            </div>
          </div>
        )}
        {!editable && (
          <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            This bill is read-only because of its status or recorded payments.
          </div>
        )}
        <form onSubmit={save}>
          <fieldset
            disabled={!editable || saving || Boolean(pendingLinkBillId)}
            className="space-y-6"
          >
            <section className="rounded-2xl border border-slate-200 bg-white p-6">
              <h2 className="mb-6 text-base font-semibold text-slate-900">
                Bill information
              </h2>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <Label>Vendor *</Label>
                  <select
                    required
                    value={form.vendor_id}
                    onChange={(e) => updateForm("vendor_id", e.target.value)}
                    className={fieldClass}
                  >
                    <option value="">Select vendor</option>
                    {vendors
                      .filter((v) => v.is_active || v.id === form.vendor_id)
                      .map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.vendor_name}
                          {!v.is_active ? " (Inactive)" : ""}
                        </option>
                      ))}
                  </select>
                  {!vendors.some((v) => v.is_active) && (
                    <p className="mt-2 text-xs text-amber-600">
                      Add an active vendor before creating a bill.
                    </p>
                  )}
                  {sourceReceiptId && !form.vendor_id && aiVendorName && (
                    <p className="mt-2 text-xs text-amber-700">
                      AI detected "{aiVendorName}", but no unique active vendor
                      match was found. Select the correct vendor before saving.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Bill number *</Label>
                  <input
                    required
                    className={fieldClass}
                    placeholder="e.g. BILL-001"
                    value={form.bill_number}
                    onChange={(e) => updateForm("bill_number", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Currency</Label>
                  <select
                    className={fieldClass}
                    value={form.currency}
                    onChange={(e) => updateForm("currency", e.target.value)}
                  >
                    {["USD", "PHP", "CAD", "EUR", "GBP", "AUD"]
                      .concat(
                        !["USD", "PHP", "CAD", "EUR", "GBP", "AUD"].includes(
                          form.currency,
                        )
                          ? [form.currency]
                          : [],
                      )
                      .map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <Label>Tax rounding method</Label>
                  <select
                    className={fieldClass}
                    value={form.tax_rounding_method}
                    onChange={(e) =>
                      updateForm("tax_rounding_method", e.target.value)
                    }
                  >
                    <option value="per_line">Round each line</option>
                    <option value="invoice">Round invoice tax once</option>
                  </select>
                  <p className="mt-1 text-xs text-slate-500">
                    Choose the method used on the supplier's invoice.
                  </p>
                </div>
                <div>
                  <Label>Bill date *</Label>
                  <input
                    type="date"
                    required
                    className={fieldClass}
                    value={form.bill_date}
                    onChange={(e) => updateForm("bill_date", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Due date *</Label>
                  <input
                    type="date"
                    required
                    className={fieldClass}
                    value={form.due_date}
                    onChange={(e) => updateForm("due_date", e.target.value)}
                  />
                </div>
                <div>
                  <Label>Internal notes</Label>
                  <input
                    className={fieldClass}
                    placeholder="Optional notes"
                    value={form.notes}
                    onChange={(e) => updateForm("notes", e.target.value)}
                  />
                </div>
              </div>
            </section>
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-6 py-5">
                <div>
                  <h2 className="font-semibold text-slate-900">Line items</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    Enter the products or services billed by your vendor.
                  </p>
                </div>
                <span className="text-xs text-slate-400">
                  {items.length} {items.length === 1 ? "item" : "items"}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-left">
                  <thead className="bg-slate-50 text-xs font-semibold text-slate-500">
                    <tr>
                      <th className="px-5 py-3">Description</th>
                      <th className="w-24 px-3 py-3">Qty</th>
                      <th className="w-32 px-3 py-3">Unit price</th>
                      <th className="w-24 px-3 py-3">Tax %</th>
                      <th className="w-36 px-3 py-3 text-right">Amount</th>
                      <th className="w-14 px-3 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item.key} className="border-t border-slate-100">
                        <td className="px-5 py-3">
                          <input
                            required
                            className={fieldClass}
                            placeholder="Item or service description"
                            value={item.description}
                            onChange={(e) =>
                              updateItem(
                                item.key,
                                "description",
                                e.target.value,
                              )
                            }
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            required
                            type="number"
                            min="0.0001"
                            step="0.0001"
                            className={fieldClass}
                            value={item.quantity}
                            onChange={(e) =>
                              updateItem(item.key, "quantity", e.target.value)
                            }
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            required
                            type="number"
                            min="0"
                            step="0.0001"
                            className={fieldClass}
                            value={item.unit_price}
                            onChange={(e) =>
                              updateItem(item.key, "unit_price", e.target.value)
                            }
                          />
                        </td>
                        <td className="px-3 py-3">
                          <input
                            required
                            type="number"
                            min="0"
                            max="100"
                            step="0.0001"
                            className={fieldClass}
                            value={item.tax_rate}
                            onChange={(e) =>
                              updateItem(item.key, "tax_rate", e.target.value)
                            }
                          />
                        </td>
                        <td className="px-3 py-3 text-right text-sm font-semibold text-slate-800">
                          {money(
                            calculatedLineItems[index]?.subtotal ?? 0,
                            form.currency,
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <button
                            type="button"
                            disabled={items.length === 1}
                            onClick={() =>
                              setItems((prev) =>
                                prev.filter((x) => x.key !== item.key),
                              )
                            }
                            className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-30"
                            title="Remove line"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-col gap-6 border-t border-slate-100 p-6 sm:flex-row sm:items-start sm:justify-between">
                <button
                  type="button"
                  onClick={() => setItems((prev) => [...prev, newItem()])}
                  className="inline-flex items-center gap-2 self-start text-sm font-semibold text-blue-600 hover:text-blue-700"
                >
                  <FiPlus /> Add line item
                </button>
                <div className="w-full max-w-xs space-y-3 text-sm">
                  <div className="flex justify-between text-slate-500">
                    <span>Subtotal</span>
                    <span>{money(totals.subtotal, form.currency)}</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>Tax</span>
                    <span>{money(totals.tax, form.currency)}</span>
                  </div>
                  <div className="flex justify-between border-t border-slate-200 pt-3 text-base font-bold text-slate-900">
                    <span>Total</span>
                    <span>{money(totals.total, form.currency)}</span>
                  </div>
                  {aiReconciliation && (
                    <div
                      className={`rounded-xl border p-3 text-xs ${aiReconciliation.matches ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}
                    >
                      <p className="font-semibold">
                        {aiReconciliation.matches
                          ? "Invoice total matches"
                          : "Invoice total requires review"}
                      </p>
                      {aiReconciliation.available ? (
                        <div className="mt-2 space-y-1">
                          <div className="flex justify-between">
                            <span>Original invoice</span>
                            <span>
                              {money(
                                aiReconciliation.sourceTotal,
                                aiReconciliation.sourceCurrency,
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Calculated bill</span>
                            <span>{money(totals.total, form.currency)}</span>
                          </div>
                          {form.currency ===
                            aiReconciliation.sourceCurrency && (
                            <div className="flex justify-between font-semibold">
                              <span>Difference</span>
                              <span>
                                {money(
                                  Math.abs(aiReconciliation.differenceCents) /
                                    100,
                                  form.currency,
                                )}
                              </span>
                            </div>
                          )}
                          {!aiReconciliation.matches && (
                            <p className="pt-2 leading-5">
                              Review the extracted prices, tax rates, and
                              invoice rounding. Do not change an amount solely
                              to force the totals to match.
                            </p>
                          )}
                        </div>
                      ) : (
                        <p className="mt-2">{aiReconciliation.message}</p>
                      )}
                    </div>
                  )}
                  {currentBill && (
                    <>
                      <div className="flex justify-between text-slate-500">
                        <span>Recorded payments</span>
                        <span>
                          {money(currentBill.amount_paid, form.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between font-semibold text-slate-900">
                        <span>Saved balance due</span>
                        <span>
                          {money(currentBill.balance_due, form.currency)}
                        </span>
                      </div>
                    </>
                  )}
                  <p className="text-xs leading-relaxed text-slate-400">
                    Line amounts exclude tax, matching the supplier invoice. The
                    database calculates the final saved totals.
                  </p>
                </div>
              </div>
            </section>
          </fieldset>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setView("list");
                setError("");
              }}
              className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            >
              Back to bills
            </button>
            {currentBill?.status === "draft" && (
              <button
                type="button"
                disabled={saving || finalizing}
                onClick={() => setShowFinalizeModal(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-5 py-3 text-sm font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              >
                <FiCheckCircle /> Finalize bill
              </button>
            )}
            {currentBill &&
              ["unpaid", "partially_paid"].includes(currentBill.status) &&
              Number(currentBill.balance_due) > 0 && (
                <button
                  type="button"
                  disabled={saving || recordingPayment}
                  onClick={() => {
                    setError("");
                    setPaymentForm({
                      amount: Number(currentBill.balance_due).toFixed(2),
                      payment_date: today(),
                      payment_method: "bank_transfer",
                      reference_number: "",
                      notes: "",
                    });
                    setShowPaymentModal(true);
                  }}
                  className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                >
                  <FiDollarSign /> Record payment
                </button>
              )}
            {editable && (
              <button
                type="submit"
                disabled={
                  saving ||
                  Boolean(pendingLinkBillId) ||
                  !vendors.some((v) => v.is_active) ||
                  (sourceReceiptId && !aiReconciliation?.matches) ||
                  (aiReviewRequired && !aiReviewed)
                }
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? <FiLoader className="animate-spin" /> : <FiSave />}
                {saving
                  ? "Saving..."
                  : editingId
                    ? "Save changes"
                    : "Save draft"}
              </button>
            )}
          </div>
        </form>
        {showFinalizeModal && currentBill && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <FiCheckCircle size={24} />
              </div>
              <h3 className="text-lg font-bold text-slate-900">
                Finalize this bill?
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                Bill <strong>{currentBill.bill_number}</strong> will change from
                Draft to Unpaid. Make sure all details and line items have been
                saved.
              </p>
              <div className="mt-4 rounded-xl bg-slate-50 p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Bill total</span>
                  <span className="font-semibold text-slate-900">
                    {money(currentBill.total, currentBill.currency)}
                  </span>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  disabled={finalizing}
                  onClick={() => setShowFinalizeModal(false)}
                  className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={finalizing}
                  onClick={handleFinalize}
                  className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {finalizing ? (
                    <FiLoader className="animate-spin" />
                  ) : (
                    <FiCheckCircle />
                  )}
                  {finalizing ? "Finalizing..." : "Confirm finalization"}
                </button>
              </div>
            </div>
          </div>
        )}
        {showPaymentModal && currentBill && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4">
            <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-bold text-slate-900">
                Record vendor payment
              </h3>
              <p className="mt-2 text-sm text-slate-500">
                Record a payment made to the vendor for bill{" "}
                <strong>{currentBill.bill_number}</strong>.
              </p>
              <div className="mt-4 rounded-xl bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Outstanding balance</p>
                <p className="mt-1 text-xl font-bold text-slate-900">
                  {money(currentBill.balance_due, currentBill.currency)}
                </p>
              </div>
              <form onSubmit={handleRecordPayment} className="mt-5 space-y-4">
                <div>
                  <Label>Payment amount *</Label>
                  <input
                    type="number"
                    min="0.01"
                    max={currentBill.balance_due}
                    step="0.01"
                    required
                    className={fieldClass}
                    value={paymentForm.amount}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>Payment date *</Label>
                  <input
                    type="date"
                    required
                    className={fieldClass}
                    value={paymentForm.payment_date}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        payment_date: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>Payment method *</Label>
                  <select
                    className={fieldClass}
                    value={paymentForm.payment_method}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        payment_method: e.target.value,
                      }))
                    }
                  >
                    <option value="bank_transfer">Bank transfer</option>
                    <option value="cash">Cash</option>
                    <option value="check">Check</option>
                    <option value="card">Card</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Reference number</Label>
                  <input
                    className={fieldClass}
                    placeholder="Optional transaction reference"
                    value={paymentForm.reference_number}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        reference_number: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <textarea
                    rows={2}
                    className={fieldClass}
                    placeholder="Optional payment notes"
                    value={paymentForm.notes}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    disabled={recordingPayment}
                    onClick={() => setShowPaymentModal(false)}
                    className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={recordingPayment}
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {recordingPayment && <FiLoader className="animate-spin" />}
                    {recordingPayment ? "Recording..." : "Record payment"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        {currentBill && (
          <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="border-b border-slate-100 px-6 py-5">
              <h2 className="font-semibold text-slate-900">Payment history</h2>
              <p className="mt-1 text-xs text-slate-500">
                Payments recorded against this vendor bill.
              </p>
            </div>
            {payments.length === 0 ? (
              <p className="px-6 py-8 text-sm text-slate-500">
                No payments recorded yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[650px] text-left text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500">
                    <tr>
                      <th className="px-6 py-3">Date</th>
                      <th className="px-5 py-3">Method</th>
                      <th className="px-5 py-3">Reference</th>
                      <th className="px-6 py-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {payments.map((payment) => (
                      <tr key={payment.id}>
                        <td className="px-6 py-4">
                          {dateLabel(payment.payment_date)}
                        </td>
                        <td className="px-5 py-4 capitalize">
                          {payment.payment_method.replaceAll("_", " ")}
                        </td>
                        <td className="px-5 py-4 text-slate-500">
                          {payment.reference_number || "—"}
                        </td>
                        <td className="px-6 py-4 text-right font-semibold">
                          {money(payment.amount, currentBill.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl pb-24">
      <div className="mb-7 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Bills
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Track vendor bills, due dates and outstanding balances.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
        >
          <FiPlus size={17} /> New bill
        </button>
      </div>
      {error && (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          <FiAlertCircle />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError("")}>
            <FiX />
          </button>
        </div>
      )}
      {success && (
        <div className="mb-5 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
          <FiCheckCircle />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess("")}>
            <FiX />
          </button>
        </div>
      )}
      <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title="Total bills"
          value={summary.count}
          icon={FiFileText}
          detail="Excluding voided bills"
        />
        <SummaryCard
          title="Outstanding"
          value={summaryMoney(summary.outstanding)}
          icon={FiDollarSign}
          detail="Unpaid and partially paid bills"
        />
        <SummaryCard
          title="Overdue"
          value={summaryMoney(summary.overdue)}
          icon={FiClock}
          detail="Past due with outstanding balance"
        />
        <SummaryCard
          title="Draft bills"
          value={summary.drafts}
          icon={FiEdit2}
          detail="Not yet finalized"
        />
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-5">
          <div className="relative w-full max-w-sm">
            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${fieldClass} pl-10`}
              placeholder="Search bills or vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              className={fieldClass}
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="unpaid">Unpaid</option>
              <option value="partially_paid">Partially paid</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="void">Void</option>
            </select>
            <button
              onClick={loadData}
              disabled={loading}
              title="Refresh"
              className="rounded-xl border border-slate-200 p-3 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
            >
              <FiRefreshCw className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
        {loading || opening ? (
          <div className="flex min-h-64 items-center justify-center gap-3 text-sm text-slate-500">
            <FiLoader className="animate-spin" /> Loading bills...
          </div>
        ) : visibleBills.length === 0 ? (
          <div className="flex min-h-80 flex-col items-center justify-center px-6 text-center">
            <div className="mb-4 rounded-2xl bg-blue-50 p-5 text-blue-600">
              <FiFileText size={28} />
            </div>
            <h3 className="font-semibold text-slate-900">
              {bills.length === 0 ? "No bills yet" : "No matching bills"}
            </h3>
            <p className="mt-2 max-w-sm text-sm text-slate-500">
              {bills.length === 0
                ? "Create your first vendor bill to start tracking accounts payable."
                : "Try changing your search or status filter."}
            </p>
            {bills.length === 0 && (
              <button
                onClick={openCreate}
                className="mt-5 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
              >
                Create first bill
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[850px] text-left">
                <thead className="bg-slate-50 text-xs font-semibold text-slate-500">
                  <tr>
                    <th className="px-6 py-4">Bill number</th>
                    <th className="px-5 py-4">Vendor</th>
                    <th className="px-5 py-4">Bill date</th>
                    <th className="px-5 py-4">Due date</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4 text-right">Total</th>
                    <th className="px-5 py-4 text-right">Balance due</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {visibleBills.map((bill) => (
                    <tr
                      key={bill.id}
                      className="cursor-pointer hover:bg-slate-50/70"
                      onClick={() => openBill(bill)}
                    >
                      <td className="px-6 py-4 text-sm font-semibold text-blue-600">
                        {bill.bill_number}
                      </td>
                      <td className="px-5 py-4 text-sm font-medium text-slate-800">
                        {vendorMap[bill.vendor_id]?.vendor_name ||
                          "Unknown vendor"}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-500">
                        {dateLabel(bill.bill_date)}
                      </td>
                      <td className="px-5 py-4 text-sm text-slate-500">
                        {dateLabel(bill.due_date)}
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge bill={bill} />
                      </td>
                      <td className="px-5 py-4 text-right text-sm font-medium text-slate-800">
                        {money(bill.total, bill.currency)}
                      </td>
                      <td className="px-5 py-4 text-right text-sm font-semibold text-slate-900">
                        {money(bill.balance_due, bill.currency)}
                      </td>
                      <td className="px-5 py-4 text-slate-400">
                        <FiArrowUpRight />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-slate-100 px-6 py-4 text-xs text-slate-500">
              Showing {visibleBills.length} of {bills.length} bills
            </div>
          </>
        )}
      </div>
    </div>
  );
}